/**
 * One message in the dock transcript, with everything the agent attached to it.
 *
 * Extracted from `Dock.tsx` by TASK-794. The bubble plus four conditional
 * side-channel renderers is a single responsibility — "draw this message and its
 * attachments" — and it was inlined in a 637-line component alongside transport,
 * scope resolution and the send path.
 *
 * Every branch here is keyed off the message's own `side` payload, except the
 * two that need the PLACEMENT (meeting cards, KYC proposals). That distinction
 * is load-bearing and is why those arrive as props rather than being resolved
 * here — see `placementClientId`.
 */

import React from 'react';
import { ChatBubble } from './ChatBubble';
import { DockMessageExtras } from './DockMessageExtras';
import { MeetingSideCards } from './MeetingSideCards';
import { KYCProposalCards } from './KYCProposalCards';
import { ArtifactCard } from '../canvas/widgets/ArtifactCard';
import type { ChatUIMessage, RequestedPrompt } from '../../stores/createChatStore';
import type { DockSideChannels } from '../../stores/dockChatStores';
import type { ExtractionProposalItem } from '../../stores/kycBridge';

export interface DockMessageProps {
  message: ChatUIMessage<DockSideChannels>;
  /** The one send pipeline — follow-up chips and Revise both go through it. */
  onQuery: (prompt: RequestedPrompt) => void;
  isMeetingScope: boolean;
  /**
   * The client id from the PLACEMENT, never from the thread. Under a task thread
   * the scope's `entityId` is a task id, and writing proposals against it would
   * be a cross-entity write, not a mislabel (TASK-769).
   */
  placementClientId: string | null;
}

export const DockMessage: React.FC<DockMessageProps> = ({
  message: m,
  onQuery,
  isMeetingScope,
  placementClientId,
}) => (
  <div className="space-y-2">
    {/* TASK-767: the advisor sees what they asked for, not the routing payload. */}
    <ChatBubble role={m.role} content={m.displayContent ?? m.content} />
    {m.role === 'assistant' && m.side && (
      <DockMessageExtras
        artifacts={m.side.artifacts}
        actions={m.side.actions}
        sources={m.side.sources}
        // TASK-778: a follow-up suggestion is a message — it goes through the
        // one send pipeline, like MeetingSideCards' onRevise. It used to be
        // dispatched as a `chat:query` event nothing listened for.
        onQuery={onQuery}
      />
    )}
    {/* TASK-768: the creation result, inline in the thread that produced it. */}
    {m.role === 'assistant' &&
      m.side?.artifactCards?.map((props, i) => (
        <div key={`${m.id}-artifact-card-${i}`} data-testid="dock-artifact-card">
          <ArtifactCard {...props} />
        </div>
      ))}
    {/* TASK-869: task proposals render on EVERY dock surface. `propose_action` is granted
        wherever a task can be claimed, and `ActionProposalCard` needs no meeting context —
        Apply POSTs /api/tasks from the proposal's own data. Gated on the meeting scope, the
        backend put a task up and the advisor saw only prose saying so. The meeting-shaped
        half (`proposalActions`, whose blocked cards recover into the prep composer) stays
        scoped. */}
    {m.role === 'assistant' && m.side && (
      <MeetingSideCards
        actionProposals={m.side.actionProposals}
        proposalActions={isMeetingScope ? m.side.proposalActions : undefined}
        onRevise={onQuery}
      />
    )}
    {/* Any client placement can apply these: the write needs a client, not the KYC screen. */}
    {m.role === 'assistant' && placementClientId && m.side?.extractionProposals && (
      <KYCProposalCards
        clientId={placementClientId}
        proposals={m.side.extractionProposals as ExtractionProposalItem[]}
      />
    )}
  </div>
);
